/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { Dispatch } from '@reduxjs/toolkit';

import type { STEP_NAMES } from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import * as flowService from '../services/flowService';

import {
  createErrorCallback,
  createSuccessCallback,
} from './stepCallbackFactory';

interface ApiServices {
  initiateOnboard?: (payload: unknown) => Promise<unknown>;
  testConnection?: (payload: unknown) => Promise<unknown>;
  saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
}

/**
 * Generic factory function for step configurations
 */
const createAllAccountStepConfigPayload = (
  stepName: string,
  nextStep: string
) => {
  // Map stepName to STEP_NAMES key
  const stepNameToKey: Record<string, string> = {
    CreateAccount: 'CREATE_ACCOUNT',
    UpdateAccount: 'UPDATE_ACCOUNT',
    EnableAccount: 'ENABLE_ACCOUNT',
    DisableAccount: 'DISABLE_ACCOUNT',
    RemoveAccount: 'REMOVE_ACCOUNT',
  };

  return {
    stepName,
    nextStep,
    transitionMessage: '',
    apiCall: async (services: ApiServices, payload: unknown) => {
      const payloadData = payload as {
        dispatch: Dispatch;
        accountData: Record<string, unknown>;
        getState: () => unknown;
      };

      if (services.saveUseCaseConfiguration) {
        const state = payloadData.getState() as {
          copilot?: { endpointKey?: number; ui?: { endpointKey?: number } };
        };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const endpointKey =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          state.copilot?.endpointKey || state.copilot?.ui?.endpointKey;

        if (endpointKey) {
          // Create a copy and remove mappings from payload
          const cleanAccountData = { ...payloadData.accountData };

          delete cleanAccountData.mappings;

          return services.saveUseCaseConfiguration({
            applicationtype: 'CUA',
            endpointKey,
            body: cleanAccountData,
          });
        }
      }

      return Promise.resolve({ success: true });
    },
    payloadBuilder: (
      formData: Record<string, unknown>,
      state?: CopilotState
    ) => ({
      accountData: formData[stepName] ?? {},
      appTypeKey: state?.appTypeKey ?? 85,
      draftType: 0,
    }),
    successCallback: (
      dispatch: Dispatch,
      result: unknown,
      getState: () => unknown
    ) => {
      // Dispatch completedStep first
      dispatch({ type: 'copilot/completedStep', payload: stepName });

      const callback = createSuccessCallback(dispatch, getState, {
        currentStep: stepNameToKey[stepName] as keyof typeof STEP_NAMES,
        nextStep: nextStep as keyof typeof STEP_NAMES,
        message: `Moving on to next step to  ${nextStep
          .toLowerCase()
          .replace('_', ' ')} ...`,
        delay: 2000,
      });

      callback(result);
    },
    errorCallback: (dispatch: Dispatch, errorMessage: string) => {
      const callback = createErrorCallback(dispatch, stepName);

      callback(errorMessage);
    },
  };
};

const STEP_CONFIGS = {
  CONNECTION: {
    stepName: 'Connection',
    nextStep: 'CREATE_ACCOUNT' as const,
    transitionMessage: '',
    apiCall: async (services: ApiServices, payload: unknown) => {
      const payloadData = payload as {
        dispatch: Dispatch;
        generalData: Record<string, unknown>;
        connectionData: Record<string, unknown>;
        getState: () => unknown;
        userPrompt?: string; // Add userPrompt parameter
      };

      return flowService.handleOnboardingFlow(
        payloadData.dispatch,
        services,
        payloadData.generalData,
        payloadData.connectionData,
        createSuccessCallback(payloadData.dispatch, payloadData.getState, {
          currentStep: 'CONNECTION',
          nextStep: 'CREATE_ACCOUNT',
          message: 'Moving on to next step to create an account.',
          delay: 2000,
        }),
        createErrorCallback(payloadData.dispatch, 'Connection'),
        payloadData.userPrompt, // Pass userPrompt as 7th parameter
        payloadData.getState
      );
    },
    payloadBuilder: (
      formData: Record<string, unknown>,
      state?: CopilotState
    ) => ({
      generalData: formData.General ?? {},
      connectionData: formData.Connection ?? {},
      parsedTemplates: state?.parsedTemplates ?? {},
      appTypeKey: state?.appTypeKey ?? 85,
      draftType: 0,
    }),
  },

  // Account step configurations using generic factory
  CreateAccount: createAllAccountStepConfigPayload(
    'CreateAccount',
    'UPDATE_ACCOUNT'
  ),
  UpdateAccount: createAllAccountStepConfigPayload(
    'UpdateAccount',
    'ENABLE_ACCOUNT'
  ),
  EnableAccount: createAllAccountStepConfigPayload(
    'EnableAccount',
    'DISABLE_ACCOUNT'
  ),
  DisableAccount: createAllAccountStepConfigPayload(
    'DisableAccount',
    'REMOVE_ACCOUNT'
  ),
  RemoveAccount: createAllAccountStepConfigPayload(
    'RemoveAccount',
    'ADD_ACCESS'
  ),
} as const;

// Add index signature for dynamic access
type StepConfigsWithIndex = Record<string, unknown>;

export default STEP_CONFIGS as typeof STEP_CONFIGS & StepConfigsWithIndex;
